import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { getDataSourceLogo } from '@atlassian/third-party-connector-configuration/connector-display-utils';
import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { DynamicConnectorIllustration } from '../assets/DynamicConnectorIllustration.tsx';
import type { ConnectorPromoConfig } from '../connector-promo-config-types.ts';

const messages = defineMessages({
	heading: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPageCard.heading',
		defaultMessage: 'Your team’s search results could be better',
		description: 'Heading for the post-connect promo card on the full search page',
	},
	body: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPageCard.body',
		defaultMessage:
			"You've connected {connectedAppName}—now search across {nextConnectorName} from Confluence.",
		description:
			'Body text for the post-connect page card with interpolation for connected and next app names',
	},
	setupLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPageCard.setupLabel',
		defaultMessage: 'Setup {nextConnectorName}',
		description:
			'Primary CTA button label on the post-connect page card, includes the next connector name',
	},
	maybeLaterLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPageCard.maybeLaterLabel',
		defaultMessage: 'Maybe later',
		description: 'Secondary CTA button label to dismiss the promo temporarily',
	},
});

type ConnectorPostConnectPageCardProps = Pick<
	ConnectorPromoConfig,
	'connectedAppName' | 'nextRecommendedConnector' | 'onSetupConnector' | 'onMaybeLater'
> & {
	testId?: string;
};

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'stretch',
		backgroundColor: token('color.background.information'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		paddingInline: token('space.200'),
		paddingBlock: token('space.250'),
		flex: 1,
	},
	textGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		marginBottom: token('space.200'),
	},
	illustration: {
		display: 'flex',
		alignItems: 'flex-end',
		alignSelf: 'flex-end',
		flexShrink: 0,
	},
});

export const ConnectorPostConnectPageCard = ({
	connectedAppName,
	nextRecommendedConnector,
	onSetupConnector,
	onMaybeLater,
	testId = 'connector-post-connect-page-card',
}: ConnectorPostConnectPageCardProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const NextConnectorLogoComponent = nextRecommendedConnector?.key
		? getDataSourceLogo(nextRecommendedConnector.key)
		: undefined;

	const handlePrimaryClick = useCallback(() => {
		if (!nextRecommendedConnector) {
			return;
		}
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorPostConnectPageCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: {
					connectorKey: nextRecommendedConnector.key,
					container: 'ConnectorPromoSearchPage',
					testId: `${testId}--primary-cta`,
				},
			},
		}).fire();
		onSetupConnector(nextRecommendedConnector.key);
	}, [nextRecommendedConnector, onSetupConnector, createEvent, testId]);

	const handleMaybeLater = useCallback(() => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorPostConnectPageCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: {
					connectorKey: nextRecommendedConnector?.key,
					container: 'ConnectorPromoSearchPage',
					testId: `${testId}--secondary-cta`,
				},
			},
		}).fire();
		onMaybeLater();
	}, [nextRecommendedConnector, onMaybeLater, createEvent, testId]);

	if (!nextRecommendedConnector) {
		return null;
	}

	return (
		<Flex xcss={styles.container} testId={testId}>
			<Stack xcss={styles.content}>
				<Stack xcss={styles.textGroup}>
					<Text size="large" weight="bold">
						{formatMessage(messages.heading)}
					</Text>
					<Text color="color.text.subtle">
						{formatMessage(messages.body, {
							connectedAppName: connectedAppName || '',
							nextConnectorName: nextRecommendedConnector.displayName,
						})}
					</Text>
				</Stack>
				<Inline space="space.075" alignBlock="center">
					<Button
						appearance="primary"
						onClick={handlePrimaryClick}
						testId={`${testId}--primary-cta`}
					>
						{formatMessage(messages.setupLabel, {
							nextConnectorName: nextRecommendedConnector.displayName,
						})}
					</Button>
					<Button
						appearance="subtle"
						onClick={handleMaybeLater}
						testId={`${testId}--secondary-cta`}
					>
						{formatMessage(messages.maybeLaterLabel)}
					</Button>
				</Inline>
			</Stack>
			<Box xcss={styles.illustration}>
				<DynamicConnectorIllustration dynamicIcon={NextConnectorLogoComponent} />
			</Box>
		</Flex>
	);
};
